import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Product } from '../services/api';
import { products as hardcodedProducts } from '../data/products';

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = () => {
    if (!id) {
      setError('Product ID is required');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const found = hardcodedProducts.find(p => p.id === parseInt(id));
    if (found) {
      setProduct(found);
    } else {
      setError('Product not found');
    }
    setLoading(false);
  };

  const openDemo = () => {
    const url = product?.demoUrl || 'https://app.vitalhivetech.com';
    window.open(url, '_blank');
  };

  const contactSales = () => {
    const subject = encodeURIComponent(`Product Inquiry: ${product?.name || ''}`);
    window.location.href = `mailto:info@vitalhive.com?subject=${subject}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary mx-auto mb-4"></div>
          <p className="text-primaryLight">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-exclamation-circle text-red-500 text-4xl mb-4"></i>
          <p className="text-red-500 mb-4">{error || 'Product not found'}</p>
          <button 
            onClick={loadProduct}
            className="bg-secondary text-primary px-6 py-2 rounded-lg font-semibold hover:bg-secondaryDark transition-colors duration-300"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-8">
        {/* Product Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            {product.isNew && (
              <span className="bg-badgePrimary text-white px-3 py-1 rounded-full text-sm font-semibold">
                New
              </span>
            )}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-primary">{product.name}</h1>
        </div>

        {/* Product Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="space-y-6">
            <div className="relative rounded-2xl overflow-hidden shadow-card">
              <img 
                src={product.imageUrl} 
                alt={product.name}
                className="w-full h-96 object-cover"
              />
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-8">
            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              {product.demoUrl && (
                <button 
                  onClick={openDemo}
                  className="flex-1 bg-secondary text-primary font-semibold py-3 px-6 rounded-lg hover:bg-secondaryDark transition-colors duration-300 flex items-center justify-center space-x-2"
                >
                  <i className="fas fa-play-circle"></i>
                  <span>Try Demo</span>
                </button>
              )}
              <button 
                onClick={contactSales}
                className="flex-1 bg-primary text-white font-semibold py-3 px-6 rounded-lg hover:bg-primaryDark transition-colors duration-300 flex items-center justify-center space-x-2"
              >
                <i className="fas fa-envelope"></i>
                <span>Contact</span>
              </button>
            </div>

            {/* Description */}
            <div>
              <h2 className="text-2xl font-bold text-primary mb-4">Description</h2>
              <p className="text-primaryLight leading-relaxed">{product.description}</p>
            </div>

            {/* Features */}
            <div>
              <h2 className="text-2xl font-bold text-primary mb-4">Key Features</h2>
              <ul className="space-y-3">
                {product.features.map((feature: string, index: number) => (
                  <li key={index} className="flex items-start space-x-3">
                    <i className="fas fa-check text-secondary mt-1"></i>
                    <span className="text-primaryLight">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Category */}
            <div>
              <h2 className="text-2xl font-bold text-primary mb-4">Category</h2>
              <span className="inline-block bg-secondaryLight text-primary px-4 py-2 rounded-lg font-semibold">
                {product.category}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductDetail; 